import React, { useState, useEffect, useRef } from 'react';
import { PageView } from '../types';
import { NetworkTopologyVisualizer } from '../components/NetworkTopologyVisualizer';
import { BadgeHeroCard } from '../components/BadgeHeroCard';
import { parseBadgeScan, logBadgeScan, BadgeScanData } from '../utils/badgeTracker';
import { 
  Pause, 
  Play, 
  ShieldCheck, 
  Lock, 
  Cpu, 
  Database, 
  Server, 
  ArrowRight, 
  Terminal
} from 'lucide-react';

interface HomePageProps {
  setActivePage: (page: PageView) => void;
  onOpenLeadModal: () => void;
}

interface TelemetryLogEntry {
  id: string;
  timestamp: string;
  dot: 'blue' | 'green' | 'red' | 'amber';
  device: string;
  msg: string;
  isAlert?: boolean;
}

const TELEMETRY_POOL: Omit<TelemetryLogEntry, 'id' | 'timestamp'>[] = [
  { dot: 'blue', device: '[POLLER-NODE]', msg: 'Bypassed heavy SNMP walk. Executed lightweight SSH terminal scrape on core OLT' },
  { dot: 'green', device: '[MUSE-FORGE]', msg: 'Parsed raw terminal output into active telemetry schema in 1.2s' },
  { dot: 'red', device: '[AI-CLUSTER]', msg: 'Suppressed 214 downstream PON alerts. Root cause isolated to Card 4 Slot 5', isAlert: true },
  { dot: 'blue', device: '[DATA-LAKE]', msg: 'Normalizing mixed vendor DOCSIS and PON telemetry into a single pane of glass' },
  { dot: 'amber', device: '[CASA-C100G]', msg: 'CMTS upstream signal to noise ratio degraded below 30dB threshold' },
  { dot: 'green', device: '[SECURE-SYNC]', msg: 'Outbound TLS 1.3 connection verified. Zero inbound firewall ports exposed' },
  { dot: 'blue', device: '[CACHE-MGR]', msg: 'Uplink restored. Flushed 42 encrypted payloads from local store and forward cache' },
  { dot: 'green', device: '[MUSE-FORGE]', msg: 'Generated baseline alerting thresholds for unrecognized network hardware' },
  { dot: 'red', device: '[TOPOLOGY-MAP]', msg: 'Correlated 186 modem drops to single neighborhood feeder cable cut', isAlert: true },
  { dot: 'blue', device: '[ML-ENGINE]', msg: 'Traffic volume baseline updated based on historical trend analysis' },
  { dot: 'amber', device: '[CALIX-OLT]', msg: 'Optical receive power dropping on Port 2. Inspect physical fiber connection' },
  { dot: 'green', device: '[POLLER-POOL]', msg: 'Load balanced 6 concurrent worker processes. Local appliance CPU stable at 12%' },
  { dot: 'blue', device: '[NOKIA-7360]', msg: 'Ingesting GPON telemetry alongside legacy cable endpoints' },
  { dot: 'red', device: '[AUTH-SYNC]', msg: 'Automated credential rotation securely pushed to local isolated appliance', isAlert: true },
  { dot: 'green', device: '[PROJECT-ISO]', msg: 'Telemetry payload landed in 100% dedicated single-tenant cloud environment' },
  { dot: 'blue', device: '[MUSE-FORGE]', msg: 'Auto-detected CRC error spike above 2%. Automatic alerting threshold applied' }
];

export const HomePage: React.FC<HomePageProps> = ({ setActivePage, onOpenLeadModal }) => {
  const [scrollY, setScrollY] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [badgeData, setBadgeData] = useState<BadgeScanData>(() => parseBadgeScan());
  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 850;
  });

  useEffect(() => {
    const data = parseBadgeScan();
    setBadgeData(data);
    if (data.isBadgeScan) {
      logBadgeScan(data);
    }

    const checkMobile = () => {
      setIsMobileDevice(
        /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 850
      );
    };
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [logs, setLogs] = useState<TelemetryLogEntry[]>([
    { id: 'init-1', timestamp: '02:14:09 EST', dot: 'blue', device: '[ASR1000-PE-01]', msg: 'Ingesting SNMP v3 traps across 128 Gigabit interfaces' },
    { id: 'init-2', timestamp: '02:14:10 EST', dot: 'green', device: '[JUNOS-MX480]', msg: 'MUSE Forge matched JunOS CLI schema stanza' },
    { id: 'init-3', timestamp: '02:14:12 EST', dot: 'red', device: '[CLUSTER-ENGINE]', msg: '248 downstream CPE alerts suppressed -> 1 Root Fiber Cut Incident', isAlert: true },
    { id: 'init-4', timestamp: '02:14:14 EST', dot: 'blue', device: '[ARISTA-7280R]', msg: 'gNMI OpenConfig telemetry stream active (Sub-second sampling)' }
  ]);

  const logContainerRef = useRef<HTMLDivElement>(null);
  const poolIndexRef = useRef<number>(4);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Real-time scrolling telemetry log stream engine
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      const template = TELEMETRY_POOL[poolIndexRef.current % TELEMETRY_POOL.length];
      poolIndexRef.current += 1;

      const now = new Date();
      const hrs = String(now.getHours()).padStart(2, '0');
      const mins = String(now.getMinutes()).padStart(2, '0');
      const secs = String(now.getSeconds()).padStart(2, '0');
      const timeStr = `${hrs}:${mins}:${secs} EST`;

      const newEntry: TelemetryLogEntry = {
        id: `${Date.now()}-${Math.random()}`,
        timestamp: timeStr,
        ...template
      };

      setLogs((prev) => [...prev.slice(-14), newEntry]);
    }, 1700);

    return () => clearInterval(interval);
  }, [isPaused]);

  // Auto-scroll terminal log body to bottom as new events arrive
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTo({
        top: logContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [logs]);

  return (
    <div className="homepage-minimal">
      {/* Ambient Parallax Background Layer */}
      <div
        className="parallax-bg-layer"
        style={{ transform: `translate3d(0, ${scrollY * 0.15}px, 0)` }}
      >
        <div className="p-dot dot-1" />
        <div className="p-dot dot-2" />
        <div className="p-dot dot-3" />
        <div className="p-grid-overlay" />
      </div>

      {/* Conference Badge Mobile Hero / Verified QR Scan */}
      {(badgeData.isBadgeScan || isMobileDevice) && (
        <BadgeHeroCard
          badgeData={badgeData}
          onOpenLeadModal={onOpenLeadModal}
          setActivePage={setActivePage}
        />
      )}

      {/* 1. HERO SECTION: ABOVE THE FOLD */}
      <section id="full-site-content" className="hero-minimal">
        <div className="section-container hero-center">
          <div className="hero-badge-wrap">
            <span className="hero-target-badge font-mono">
              BUILT FOR TIER 3 &amp; 4 INDEPENDENT ISPS AND FIBER OPERATORS
            </span>
          </div>

          <h1 className="hero-headline">
            Enterprise-Grade Network Visibility. Built &amp; Priced for the Independent ISP.
          </h1>

          <p className="hero-subtext">
            Stop paying enterprise software fees across fragmented vendor tools. MUSE Telemetry unifies DOCSIS, PON, and custom routing gear into a single operational view, with automated root-cause clustering, rapid device onboarding, and zero vendor lock-in.
          </p>

          <div className="hero-actions">
            <button onClick={onOpenLeadModal} className="btn btn-primary btn-lg">
              <span>Book 15-Minute Operational Review</span>
              <ArrowRight size={16} />
            </button>

            <button onClick={() => setActivePage('forge')} className="btn btn-secondary btn-lg">
              <Terminal size={16} />
              <span>Explore Interactive Sandbox</span>
            </button>
          </div>

          {/* Trust Bar Strip */}
          <div className="hero-trust-bar">
            <div className="trust-item">
              <Lock size={14} className="trust-icon" />
              <span>100% Single-Tenant Dedicated Cloud</span>
            </div>
            <div className="trust-separator">•</div>
            <div className="trust-item">
              <ShieldCheck size={14} className="trust-icon" />
              <span>Outbound-Only Collector (Zero Inbound Ports)</span>
            </div>
            <div className="trust-separator">•</div>
            <div className="trust-item">
              <Server size={14} className="trust-icon" />
              <span>30+ Years Operator Pedigree</span>
            </div>
          </div>

          {/* Hero Live Telemetry Terminal: Auto-Scrolling Real-Time Stream */}
          <div
            className="hero-mockup-frame parallax-card"
            style={{
              transform: `translate3d(0, ${scrollY * -0.08}px, 0) rotateX(${Math.min(scrollY * 0.015, 3)}deg)`
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="mockup-header-bar">
              <div className="mac-dots">
                <span className="status-dot red" />
                <span className="status-dot amber" />
                <span className="status-dot green" />
              </div>
              <div className="mockup-title-wrap">
                <span className="mockup-title-text">live_telemetry_stream.log</span>
              </div>
              <div className="terminal-live-badge">
                <span className={`live-pulse-dot ${isPaused ? 'paused' : ''}`} />
                <span className="live-badge-text">
                  {isPaused ? 'PAUSED' : 'LIVE STREAM'}
                </span>
                <button
                  className="pause-toggle-btn"
                  onClick={() => setIsPaused(!isPaused)}
                  title={isPaused ? 'Resume Stream' : 'Pause Stream'}
                >
                  {isPaused ? <Play size={10} /> : <Pause size={10} />}
                </button>
              </div>
            </div>

            <div className="mockup-content-body terminal-stream-body" ref={logContainerRef}>
              {logs.map((log) => (
                <div key={log.id} className={`log-row animated-log-row ${log.isAlert ? 'alert-row' : ''}`}>
                  <span className={`status-dot ${log.dot}`} />
                  <span className="log-timestamp">{log.timestamp}</span>
                  <span className="log-device">{log.device}</span>
                  <span className="log-msg">{log.msg}</span>
                </div>
              ))}
              <div className="terminal-cursor-row">
                <span className="cursor-prompt">&gt;</span>
                <span className="blinking-cursor">_</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. THE VILLAIN: THE REALITY OF MIXED-VENDOR NETWORKS */}
      <section className="section-padding agitation-parallax-section">
        <div className="equipment-parallax-bg">
          <div
            className="equipment-img-layer"
            style={{ transform: `translate3d(0, ${Math.max((scrollY - 400) * 0.18, -140)}px, 0)` }}
          />
          <div className="equipment-overlay-mask" />
        </div>

        <div className="section-container relative-z">
          <div className="section-heading-glass">
            <div className="pill-tag-glass mb-2">
              <span className="status-dot red" />
              <span>THE STATUS QUO</span>
            </div>
            <h2>Mixed-Vendor Infrastructure Shouldn't Mean Multi-Dashboard Chaos.</h2>
            <p>Upgrading from DOCSIS to PON? Operating hardware from two or three different vendors? You shouldn't have to pay for multiple overlapping enterprise software licenses just to understand what's happening on your network.</p>
          </div>

          <div className="grid-3 aligned-card-grid">
            <div className="flat-card glass-card">
              <div className="card-header-dot">
                <span className="status-dot amber" />
                <span className="card-tag">01. BLIND SPOTS</span>
              </div>
              <h3>The Multi-Dashboard Blind Spot</h3>
              <p>
                During a major incident, your NOC loses critical minutes bouncing between Calix, Nokia, and Cisco dashboards trying to correlate disconnected alarms across fragmented browser tabs.
              </p>
            </div>

            <div className="flat-card glass-card">
              <div className="card-header-dot">
                <span className="status-dot red" />
                <span className="card-tag">02. PRICING SQUEEZE</span>
              </div>
              <h3>The Enterprise Pricing Squeeze</h3>
              <p>
                Legacy tools charge flat 6-figure enterprise tiers designed for Tier 1 telcos, punishing regional and independent operators as they add new grant-funded subscriber lines.
              </p>
            </div>

            <div className="flat-card glass-card">
              <div className="card-header-dot">
                <span className="status-dot red" />
                <span className="card-tag">03. ALERT STORMS</span>
              </div>
              <h3>Alert Storm Fatigue</h3>
              <p>
                A single fiber cut or upstream card reset triggers 200+ cascading alarms, masking the true root cause, overwhelming support queues, and causing costly false truck rolls.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. INTERACTIVE COMPONENT: EVENT CLUSTERING VISUALIZER */}
      <section className="section-padding border-top">
        <div className="section-container">
          <NetworkTopologyVisualizer />
        </div>
      </section>

      {/* 4. THE MECHANISM: THE UNIFIED LAYER */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading">
            <div className="pill-tag mb-2">
              <span className="status-dot green" />
              <span>THE UNIFIED LAYER</span>
            </div>
            <h2>One Operational View Across Every Device, Vendor, and Customer</h2>
            <p>MUSE Telemetry connects to your networking equipment regardless of manufacturer, normalizing telemetry and applying topology-aware correlation.</p>
          </div>

          <div className="grid-2 aligned-card-grid">
            <div className="flat-card">
              <div className="card-header-dot">
                <span className="status-dot red" />
                <span className="card-tag">TOPOLOGICAL CLUSTERING</span>
              </div>
              <h3>Event Clustering &amp; Fiber-Cut Isolation</h3>
              <p>
                When a major link fails, downstream devices lose connectivity simultaneously. MUSE Telemetry correlates physical topology with client geolocation to collapse 200+ alarms into a single root-cause incident.
              </p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <span className="status-dot green" />
                <span className="card-tag">HARDWARE FREEDOM</span>
              </div>
              <h3>Complete Hardware Independence</h3>
              <p>
                Select access gear based on price and performance rather than software compatibility. Unify modern PON OLTs, legacy cable CMTS, and core routers under one pane of glass.
              </p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <span className="status-dot amber" />
                <span className="card-tag">SCHEMA STUDIO</span>
              </div>
              <h3>MUSE Forge: Device Schemas in Minutes</h3>
              <p>
                Onboard unrecognized or custom hardware without waiting for vendor drivers. Parse raw terminal diagnostic commands into structured telemetry metrics and baselines.
              </p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <span className="status-dot blue" />
                <span className="card-tag">DUAL-PERSPECTIVE</span>
              </div>
              <h3>Outside-In &amp; Inside-Out Visibility</h3>
              <p>
                Bridge the gap between green NOC dashboards and subscriber complaints. Correlate internal switch states and routing tables with active customer-edge reachability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. ENTERPRISE SECURITY & ARCHITECTURE PANEL */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading">
            <div className="pill-tag mb-2">
              <span className="status-dot blue" />
              <span>SECURITY &amp; ARCHITECTURE</span>
            </div>
            <h2>Zero Inbound Firewall Rules. 100% Single-Tenant Isolation.</h2>
            <p>We know you cannot allow third-party cloud tools unconstrained access to your private network infrastructure. MUSE Telemetry is engineered for strict network isolation.</p>
          </div>

          <div className="grid-2 aligned-card-grid">
            <div className="flat-card">
              <div className="card-header-dot">
                <Lock size={18} className="text-blue" />
                <span className="card-tag font-mono">FIREWALL INTEGRITY</span>
              </div>
              <h3>Outbound-Only Collector Appliance</h3>
              <p>
                Deploys behind your firewall as a lightweight Docker container or hardened VM on a private IP. Communicates strictly outbound via TLS 1.3 with <strong>zero open inbound ports, no public IP, and zero NAT rule modifications.</strong>
              </p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <Database size={18} className="text-blue" />
                <span className="card-tag font-mono">DEDICATED CLOUD</span>
              </div>
              <h3>100% Single-Tenant Cloud Isolation</h3>
              <p>
                Every customer receives a dedicated project and isolated data store. Your telemetry, credentials, and topology maps are strictly private and never co-mingled in a multi-tenant SaaS database.
              </p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <Cpu size={18} className="text-blue" />
                <span className="card-tag font-mono">CONTROL-PLANE SAFETY</span>
              </div>
              <h3>CPU-Conscious Ingestion</h3>
              <p>
                Heavy SNMP walks can spike CPU on OLT and CMTS control planes. MUSE Telemetry defaults to lightweight terminal/SSH polling and targeted OID queries to protect device control planes during peak traffic.
              </p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <ShieldCheck size={18} className="text-blue" />
                <span className="card-tag font-mono">DATA RESILIENCY</span>
              </div>
              <h3>Offline Store-and-Forward</h3>
              <p>
                Local disk encryption (LUKS AES-256) spools telemetry securely during WAN backhaul disconnects, automatically flushing cached payloads to your cloud project upon reconnect.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. FOUNDER AUTHORITY & OPERATOR PEDIGREE */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="flat-card founder-home-card">
            <div className="founder-home-badge">
              <Server size={18} />
              <span>ENGINEERED BY OPERATORS</span>
            </div>
            <h2>Built by Network Engineers Who Lived the 3:00 AM Outage</h2>
            <p className="founder-home-lead">
              MUSE Telemetry wasn't created by enterprise software salespeople. It was built by network infrastructure veterans with over 30 years of hands-on experience.
            </p>
            <div className="founder-home-body">
              <p>
                From managing nationwide dial-up and broadband ingestion at America Online (AOL) to converting an early ATM network into an ISP in 2000 (Network Access Solutions, later acquired by MegaPath), we've lived the frustration of vendor lock-in, midnight config blunders, and multi-dashboard alarm storms.
              </p>
              <p className="highlight-quote">
                "When you partner with MUSE Telemetry, you don't get routed to a tier-1 support ticket queue or a commission-driven account manager. <strong>You work directly with the senior infrastructure engineer who built the platform.</strong>"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. PRODUCTION EVALUATION & CALL TO ACTION */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="flat-card eval-cta-card">
            <div className="section-heading text-center mb-4">
              <div className="pill-tag mb-2">
                <span className="status-dot green" />
                <span>PRODUCTION EVALUATION</span>
              </div>
              <h2>Prove It on Your Live Network Traffic</h2>
              <p className="eval-subtext">
                We understand the hesitation of evaluating new network monitoring software. That's why we make testing MUSE practical and risk-free.
              </p>
            </div>

            <div className="eval-steps-grid">
              <div className="eval-step">
                <div className="step-number font-mono">01</div>
                <h4>Test in the Browser Sandbox</h4>
                <p>Explore how MUSE Forge parses real terminal outputs into verified schemas in our interactive web demo.</p>
              </div>

              <div className="eval-step">
                <div className="step-number font-mono">02</div>
                <h4>15-Minute Operational Review</h4>
                <p>We map your hardware mix, review collector requirements, and calculate transparent per-subscriber pricing for your network size.</p>
              </div>

              <div className="eval-step">
                <div className="step-number font-mono">03</div>
                <h4>Founder-Led Live Pilot</h4>
                <p>Deploy the collector on a segment of your network with direct white-glove setup. Verify root-cause clustering on live traffic before committing.</p>
              </div>
            </div>

            <div className="eval-cta-actions">
              <button onClick={onOpenLeadModal} className="btn btn-primary btn-lg">
                <span>Book 15-Minute Operational Review</span>
                <ArrowRight size={16} />
              </button>

              <button onClick={() => setActivePage('forge')} className="btn btn-secondary btn-lg">
                <Terminal size={16} />
                <span>Launch MUSE Forge Sandbox</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        /* Parallax Container Layouts */
        .homepage-minimal {
          position: relative;
          overflow: hidden;
        }

        .parallax-bg-layer {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          pointer-events: none;
          z-index: 0;
          overflow: hidden;
        }

        .p-dot {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.12;
        }

        .dot-1 {
          width: 450px; height: 450px;
          background: #3b82f6;
          top: -100px; left: 15%;
        }

        .dot-2 {
          width: 500px; height: 500px;
          background: #10b981;
          top: 600px; right: 10%;
        }

        .dot-3 {
          width: 400px; height: 400px;
          background: #f59e0b;
          top: 1400px; left: 20%;
        }

        .p-grid-overlay {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 32px 32px;
          opacity: 0.5;
        }

        .relative-z {
          position: relative;
          z-index: 2;
        }

        /* Hero Target Badge */
        .hero-badge-wrap {
          margin-bottom: 1.25rem;
        }

        .hero-target-badge {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          color: #60a5fa;
          background: rgba(37, 99, 235, 0.12);
          border: 1px solid rgba(59, 130, 246, 0.3);
          padding: 0.35rem 0.85rem;
          border-radius: 20px;
        }

        /* Hero Trust Bar */
        .hero-trust-bar {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
          margin-bottom: 3.5rem;
          flex-wrap: wrap;
          font-size: 0.82rem;
          color: var(--text-muted);
        }

        .trust-item {
          display: flex;
          align-items: center;
          gap: 0.45rem;
        }

        .trust-icon {
          color: #3b82f6;
        }

        .trust-separator {
          color: #334155;
        }

        /* Equipment Parallax Background */
        .agitation-parallax-section {
          position: relative;
          overflow: hidden;
        }

        .equipment-parallax-bg {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          pointer-events: none;
          z-index: 1;
          overflow: hidden;
        }

        .equipment-img-layer {
          position: absolute;
          top: -25%;
          left: 0;
          width: 100%;
          height: 150%;
          background-image: url('/images/illustrations/Pile_of_Equipment.jpg');
          background-size: cover;
          background-position: center center;
          background-repeat: no-repeat;
          filter: brightness(1.15) contrast(1.1);
          will-change: transform;
          transition: transform 0.1s cubic-bezier(0.1, 1, 0.1, 1);
        }

        .equipment-overlay-mask {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: linear-gradient(180deg, #000000 0%, rgba(0, 0, 0, 0.3) 30%, rgba(0, 0, 0, 0.3) 70%, #000000 100%);
        }

        /* Section Heading Glass Card */
        .section-heading-glass {
          background: transparent;
          backdrop-filter: none;
          -webkit-backdrop-filter: none;
          border: none;
          padding: 0;
          margin-bottom: 2rem;
          max-width: 820px;
        }

        .pill-tag-glass {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          font-size: 0.72rem;
          font-family: var(--font-mono);
          font-weight: 700;
          letter-spacing: 0.05em;
          color: #f87171;
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
          padding: 0.25rem 0.65rem;
          border-radius: 4px;
        }

        .section-heading-glass h2 {
          font-size: 2.2rem;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.03em;
          margin-bottom: 0.6rem;
          text-shadow: 0 2px 12px rgba(0, 0, 0, 0.95), 0 0 20px rgba(0, 0, 0, 0.9);
        }

        .section-heading-glass p {
          font-size: 1.08rem;
          color: #e2e8f0;
          line-height: 1.5;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.95), 0 0 16px rgba(0, 0, 0, 0.9);
        }

        /* Aligned Card Grid */
        .aligned-card-grid {
          align-items: stretch;
        }

        .glass-card {
          background: rgba(10, 10, 10, 0.88) !important;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.12) !important;
          height: 100%;
        }

        /* Hero Styling */
        .hero-minimal {
          position: relative;
          z-index: 1;
          padding: 6.5rem 0 4.5rem;
        }

        .hero-center {
          text-align: center;
          max-width: 900px;
        }

        .hero-headline {
          font-size: 3.25rem;
          font-weight: 800;
          line-height: 1.12;
          color: #ffffff;
          letter-spacing: -0.035em;
          margin-bottom: 1.35rem;
        }

        .hero-subtext {
          font-size: 1.15rem;
          color: var(--text-muted);
          line-height: 1.6;
          max-width: 760px;
          margin: 0 auto 2.25rem;
        }

        .hero-actions {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        /* Hero Interface Frame */
        .hero-mockup-frame {
          background: #05080e;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: var(--radius-md);
          text-align: left;
          overflow: hidden;
          will-change: transform;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.9), 0 0 30px rgba(59, 130, 246, 0.08);
          transition: transform 0.1s cubic-bezier(0.1, 1, 0.1, 1);
        }

        .parallax-card {
          will-change: transform;
          transition: transform 0.15s cubic-bezier(0.1, 1, 0.1, 1);
        }

        .mockup-header-bar {
          background: #0d121c;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          padding: 0.65rem 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .mac-dots {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .mockup-title-wrap {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .mockup-title-text {
          font-family: var(--font-mono);
          font-size: 0.75rem;
          color: #94a3b8;
          font-weight: 500;
        }

        .terminal-live-badge {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.25);
          padding: 0.2rem 0.5rem;
          border-radius: 20px;
        }

        .live-pulse-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #10b981;
          box-shadow: 0 0 8px #10b981;
          animation: pulseGlow 1.8s infinite ease-in-out;
        }

        .live-pulse-dot.paused {
          background: #f59e0b;
          box-shadow: none;
          animation: none;
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.85); }
        }

        .live-badge-text {
          font-family: var(--font-mono);
          font-size: 0.68rem;
          font-weight: 600;
          color: #10b981;
          letter-spacing: 0.05em;
        }

        .pause-toggle-btn {
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          display: flex;
          align-items: center;
          padding: 2px;
          transition: color 0.15s ease;
        }

        .pause-toggle-btn:hover {
          color: #ffffff;
        }

        /* Auto-Scrolling Terminal Stream Container */
        .terminal-stream-body {
          padding: 1.25rem;
          font-family: var(--font-mono);
          font-size: 0.8rem;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
          max-height: 240px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #1e293b transparent;
        }

        .terminal-stream-body::-webkit-scrollbar {
          width: 6px;
        }

        .terminal-stream-body::-webkit-scrollbar-thumb {
          background: #1e293b;
          border-radius: 4px;
        }

        .animated-log-row {
          animation: logFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @keyframes logFadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .log-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: var(--text-muted);
          line-height: 1.4;
        }

        .log-timestamp {
          color: #64748b;
          font-size: 0.75rem;
          white-space: nowrap;
        }

        .log-device {
          color: #ffffff;
          font-weight: 600;
          white-space: nowrap;
        }

        .log-msg {
          color: #cbd5e1;
        }

        .log-row.alert-row {
          background: rgba(239, 68, 68, 0.08);
          padding: 0.35rem 0.6rem;
          border-radius: 4px;
          border-left: 2px solid #ef4444;
        }

        .terminal-cursor-row {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          padding-top: 0.25rem;
        }

        .cursor-prompt {
          color: #3b82f6;
          font-weight: 700;
        }

        .blinking-cursor {
          color: #ffffff;
          font-weight: 700;
          animation: blink 1s step-start infinite;
        }

        @keyframes blink {
          50% { opacity: 0; }
        }

        .text-blue { color: #3b82f6; }

        /* Headings */
        .section-heading {
          margin-bottom: 2.5rem;
        }

        .section-heading h2 {
          font-size: 2.1rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        /* Founder Home Card */
        .founder-home-card {
          background: #080808;
          border: 1px solid var(--border-subtle);
          padding: 3rem 2.5rem;
        }

        .founder-home-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          color: #94a3b8;
          margin-bottom: 1rem;
        }

        .founder-home-card h2 {
          font-size: 2rem;
          margin-bottom: 0.75rem;
        }

        .founder-home-lead {
          font-size: 1.1rem;
          color: var(--text-primary);
          margin-bottom: 1.5rem;
        }

        .founder-home-body {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          font-size: 0.95rem;
          line-height: 1.7;
          color: var(--text-muted);
          max-width: 900px;
        }

        .highlight-quote {
          font-size: 1.05rem;
          color: #ffffff;
          border-left: 2px solid #3b82f6;
          padding-left: 1.25rem;
          margin-top: 0.5rem;
          line-height: 1.6;
        }

        /* Production Evaluation Card */
        .eval-cta-card {
          background: linear-gradient(180deg, #0a0a0a 0%, #05080c 100%);
          border: 1px solid #1e293b;
          padding: 3.5rem 2.5rem;
          text-align: center;
        }

        .eval-subtext {
          font-size: 1.05rem;
          color: var(--text-muted);
          max-width: 700px;
          margin: 0 auto;
        }

        .eval-steps-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
          margin: 2.5rem 0;
          text-align: left;
        }

        .eval-step {
          background: #000000;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
        }

        .step-number {
          font-size: 1.4rem;
          font-weight: 700;
          color: #3b82f6;
          margin-bottom: 0.5rem;
        }

        .eval-step h4 {
          font-size: 1rem;
          color: #ffffff;
          margin-bottom: 0.4rem;
        }

        .eval-step p {
          font-size: 0.85rem;
          color: var(--text-muted);
          line-height: 1.5;
          margin: 0;
        }

        .eval-cta-actions {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        @media (max-width: 850px) {
          .hero-headline { font-size: 2.2rem; }
          .hero-minimal { padding: 4rem 0 3rem; }
          .parallax-card, .hero-mockup-frame, .equipment-img-layer { transform: none !important; }
          .log-row { flex-wrap: wrap; gap: 0.35rem; }
          .eval-steps-grid { grid-template-columns: 1fr; }
          .hero-trust-bar { flex-direction: column; gap: 0.5rem; }
          .trust-separator { display: none; }
        }
      `}</style>
    </div>
  );
};

export default HomePage;
